<docs>
A button whose whole content is one icon: a convenience wrapper around ZoneButton. There is no
default slot; pass the Zone icon name as `icon` and it is handed to ZoneIcon (see ZoneIcon for the
name space). No events of its own: your `@click` lands on the real `<button>`/`<a>` as a native
listener and is swallowed while disabled. Use ZoneButton when the button has a text label (its
`icon-only` prop covers the 32/40/48px squares this component shares with it), ZoneButtonDropdown for
a button that opens a menu of actions.

## Notes

- Accessibility: an icon is not an accessible name. Always pass an `aria-label` (it falls through to
  the real button/anchor) unless a `tooltip` already names the action.
- Icon size follows the button size, per Figma: `xs` renders a 12px glyph (ZoneIcon `xs`),
  `sm`/`md`/`lg` an 18px one (`lg`), `xl` a 24px one (`xxl`).
- The `#indicator` badge is dropped at `xs` and `sm`: a 16px/20px square has no room for it. Same
  rule as Zone::IconButton#render_indicator?, and Figma gives those two symbols no badge slot.
- Extra attrs (class included) land on the underlying element; your class is appended to the
  computed ones, not swapped for them.
- `disabled` uses `aria-disabled`, so the element stays natively clickable and ZoneButton swallows
  the click. Test intent via `aria-disabled` or an onClick spy that was not called.

Figma: [Icon button](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=1760-3042)
</docs>

<template>
  <ZoneButton
    icon-only
    :size="buttonSize"
    :size-class="sizeClass"
    :rounded="rounded"
    :variant="variant"
    :color="color"
    :disabled="disabled"
    :href="href"
    :type="type"
    :tooltip="tooltip"
  >
    <template #leading-icon>
      <ZoneIcon
        :icon="icon"
        :size="iconSize"
      />
    </template>

    <template
      v-if="$slots.indicator && showIndicator"
      #indicator
    >
      <slot name="indicator" />
    </template>
  </ZoneButton>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import ZoneButton, { type ButtonSize, type ButtonVariant, type ButtonColor } from './Button.vue';
import ZoneIcon, { type IconName } from './Icon.vue';

const SIZES = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export type IconButtonSize = (typeof SIZES)[number];

const VARIANTS = ['solid', 'outline', 'ghost'] as const;

const COLORS = ['primary', 'primary_alt', 'red', 'green', 'yellow', 'purple', 'gray', 'gray_alt', 'white'] as const;

// Figma node 1760:3042 — Size=XS|S|M|L|XL are 16/20/32/40/48px squares, the same scale
// Zone::IconButton renders on the Ruby side. The bottom two are smaller than any ZoneButton
// height, which is why these go through ZoneButton's `sizeClass` escape hatch rather than its
// `size` prop: `sizeClass` REPLACES the computed size classes, so there's never an `h-8` vs `h-4`
// pair whose winner depends on Tailwind's output order.
const SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: 'h-4 w-4 px-0',
  sm: 'h-5 w-5 px-0',
  md: 'h-8 w-8 px-0',
  lg: 'h-10 w-10 px-0',
  xl: 'h-12 w-12 px-0',
};

// Figma pairs XS with a 12px glyph (`add/12`), S/M/L with 18px (`add/18`) and XL with 24px
// (`add/24`) — ZoneIcon's `xs` (text-xs), `lg` (text-lg) and `xxl` (text-2xl).
const ICON_SIZES: Record<IconButtonSize, 'xs' | 'lg' | 'xxl'> = {
  xs: 'xs',
  sm: 'lg',
  md: 'lg',
  lg: 'lg',
  xl: 'xxl',
};

// `sizeClass` already owns the geometry, so this only keeps ZoneButton's own `size` prop honest
// for anything reading it — the three sizes the two components share line up by pixel height
// (32/40/48), and the two below that have no ZoneButton equivalent, so they borrow its smallest.
const BUTTON_SIZES: Record<IconButtonSize, ButtonSize> = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
};

// A 16px/20px square has no room for a corner badge, and Figma gives those two symbols no badge
// slot at all. Mirrors Zone::IconButton#render_indicator?.
const INDICATOR_SIZES: IconButtonSize[] = ['md', 'lg', 'xl'];
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneIconButton' });

const props = defineProps({
  /** A Material Symbols name, snake_case ('add', 'delete', 'play_arrow'). */
  icon: {
    type: String as PropType<IconName>,
    required: true,
  },
  /**
   * 16 / 20 / 32 / 40 / 48px squares, a wider scale than ZoneButton's, which bottoms out at 32px.
   */
  size: {
    type: String as PropType<IconButtonSize>,
    default: 'md',
    validator: (v: string) => SIZES.includes(v as IconButtonSize),
  },
  /**
   * Render a circle rather than a rounded rect. Defaults to `true`: the Zone spec's icon buttons
   * are round by default (Zone::IconButton does the same), unlike ZoneButton's rounded rect. Pass
   * `false` for the "Shape: Square" symbols.
   */
  rounded: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String as PropType<ButtonVariant>,
    default: 'solid',
    validator: (v: string) => VARIANTS.includes(v as ButtonVariant),
  },
  color: {
    type: String as PropType<ButtonColor>,
    default: 'primary',
    validator: (v: string) => COLORS.includes(v as ButtonColor),
  },
  /**
   * Applies `aria-disabled`, not the native attribute, so the element stays focusable and
   * hoverable, which is what lets a tooltip work on a disabled button. Clicks are still suppressed.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Renders an `<a>` instead of a `<button>`; a disabled `<a>` drops its href. */
  href: {
    type: String,
    default: null,
  },
  /** Only on `<button>`. */
  type: {
    type: String as PropType<'button' | 'submit' | 'reset'>,
    default: 'button',
  },
  /**
   * Hover text (ZoneTooltip), forwarded to ZoneButton; also names the button for screen readers.
   */
  tooltip: {
    type: String,
    default: null,
  },
});

defineSlots<{
  /** A ZoneIndicator for the corner badge: colour, border and count are yours. Dropped at `xs` and `sm`. */
  indicator?(): any;
}>();

const sizeClass = computed<string>(() => SIZE_CLASSES[props.size]);

const iconSize = computed<'xs' | 'lg' | 'xxl'>(() => ICON_SIZES[props.size]);

const buttonSize = computed<ButtonSize>(() => BUTTON_SIZES[props.size]);

const showIndicator = computed<boolean>(() => INDICATOR_SIZES.includes(props.size));
</script>
